import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { BookMarked, Loader2, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useAuth } from "@/hooks/useAuth";
import { formatApiErrorDetail } from "@/lib/errors";

export default function AuthPage({ mode = "login" }) {
  const isRegister = mode === "register";
  const nav = useNavigate();
  const { login, register } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href =
      "https://auth.emergentagent.com/?redirect=" + encodeURIComponent(redirectUrl);
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      if (isRegister) {
        await register(email, password, name);
        toast.success(`Welcome, ${name || email.split("@")[0]}!`);
      } else {
        await login(email, password);
        toast.success("Welcome back");
      }
      nav("/", { replace: true });
    } catch (err) {
      const msg = formatApiErrorDetail(err?.response?.data?.detail) || err.message;
      setError(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="min-h-screen paper-grain bg-[#FFF5FA] dark:bg-[#1E1529] flex items-center justify-center px-6 py-12"
      data-testid={isRegister ? "register-page" : "login-page"}
    >
      <div className="w-full max-w-md fade-slide-in">
        <div className="flex items-center gap-3 justify-center mb-8">
          <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-[#F472B6] to-[#A78BFA] text-white grid place-items-center shadow-[0_4px_14px_rgba(244,114,182,0.45)] -rotate-6">
            <BookMarked className="w-6 h-6" />
          </div>
          <div className="font-serif-display text-3xl font-semibold text-[#3B2350] dark:text-[#FBEFF6]">
            LitMap
          </div>
        </div>

        <div className="bg-white dark:bg-[#3B2350] border border-[#F5D0E3] dark:border-[#45325A] rounded-2xl p-8 shadow-sm">
          <div className="mb-6">
            <div className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-[#C9286F] dark:text-[#C4A1FF] font-semibold mb-2">
              <Sparkles className="w-3 h-3" /> {isRegister ? "Start your library" : "Welcome back"}
            </div>
            <h1 className="font-serif-display text-3xl font-semibold text-[#3B2350] dark:text-[#FBEFF6] leading-tight">
              {isRegister ? "Create your account." : "Sign in to your library."}
            </h1>
          </div>

          <form onSubmit={submit} className="space-y-4">
            <button
              type="button"
              data-testid="google-sign-in-button"
              onClick={handleGoogle}
              className="w-full flex items-center justify-center gap-3 h-11 bg-white hover:bg-[#FDF2F8] dark:bg-[#1E1529] dark:hover:bg-[#2E2140] border border-[#F5D0E3] dark:border-[#45325A] rounded-md text-sm font-medium text-[#3B2350] dark:text-[#FBEFF6] transition-colors"
            >
              <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
                <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6.1 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.6 6.1 29.6 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"/>
                <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.6 6.1 29.6 4 24 4 16.1 4 9.3 8.6 6.3 14.7z"/>
                <path fill="#4CAF50" d="M24 44c5.5 0 10.5-2.1 14.2-5.5l-6.6-5.4C29.6 34.9 26.9 36 24 36c-5.2 0-9.6-3.3-11.3-7.9l-6.5 5C9.2 39.4 16 44 24 44z"/>
                <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.2 4.3-4.2 5.7l6.6 5.4c-.5.4 7.3-5.3 7.3-15.1 0-1.3-.1-2.4-.4-3.5z"/>
              </svg>
              Continue with Google
            </button>

            <div className="flex items-center gap-3">
              <div className="flex-1 h-px bg-[#F5D0E3] dark:bg-[#45325A]" />
              <span className="text-xs uppercase tracking-widest text-[#7A6A8C] font-medium">
                or {isRegister ? "sign up" : "sign in"} with email
              </span>
              <div className="flex-1 h-px bg-[#F5D0E3] dark:bg-[#45325A]" />
            </div>

            {isRegister && (
              <div>
                <Label className="text-xs uppercase tracking-wide text-[#7A6A8C]">Name</Label>
                <Input
                  data-testid="auth-name-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Your name"
                  className="mt-1 bg-[#FFF5FA] dark:bg-[#1E1529] border-[#F5D0E3] dark:border-[#45325A]"
                />
              </div>
            )}
            <div>
              <Label className="text-xs uppercase tracking-wide text-[#7A6A8C]">Email</Label>
              <Input
                data-testid="auth-email-input"
                type="email"
                required
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="mt-1 bg-[#FFF5FA] dark:bg-[#1E1529] border-[#F5D0E3] dark:border-[#45325A]"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide text-[#7A6A8C]">Password</Label>
              <Input
                data-testid="auth-password-input"
                type="password"
                required
                minLength={6}
                autoComplete={isRegister ? "new-password" : "current-password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={isRegister ? "at least 6 characters" : ""}
                className="mt-1 bg-[#FFF5FA] dark:bg-[#1E1529] border-[#F5D0E3] dark:border-[#45325A]"
              />
            </div>

            {error && (
              <div
                data-testid="auth-error"
                className="text-sm text-[#C9286F] dark:text-[#F9A8D4] bg-[#FCE4F0] dark:bg-[#4A1D3A] px-3 py-2 rounded-md"
              >
                {error}
              </div>
            )}

            <Button
              data-testid="auth-submit-button"
              type="submit"
              disabled={busy}
              className="w-full bg-[#C9286F] hover:bg-[#B8246C] dark:bg-[#C4A1FF] dark:hover:bg-[#A982F0] text-white h-11 gap-2"
            >
              {busy && <Loader2 className="w-4 h-4 animate-spin" />}
              {isRegister ? "Create account" : "Sign in"}
            </Button>
          </form>

          <div className="mt-6 text-sm text-center text-[#5E4B70] dark:text-[#BBA9CF]">
            {isRegister ? (
              <>
                Already have an account?{" "}
                <Link data-testid="auth-toggle-link" to="/login" className="text-[#C9286F] dark:text-[#C4A1FF] hover:underline font-medium">
                  Sign in
                </Link>
              </>
            ) : (
              <>
                New here?{" "}
                <Link data-testid="auth-toggle-link" to="/register" className="text-[#C9286F] dark:text-[#C4A1FF] hover:underline font-medium">
                  Create an account
                </Link>
              </>
            )}
          </div>
        </div>

        <p className="text-xs text-center text-[#7A6A8C] mt-6">
          Your citations are private and only visible to you.
        </p>
      </div>
    </div>
  );
}
